import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import AIAssistant from '../components/AIAssistant';
import BatchImport from '../components/BatchImport';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Loader2,
  ChevronUp,
  ChevronDown,
  Bot,
  Sparkles,
  Upload,
  Trash2,
  CheckSquare,
  LayoutList,
  Columns3,
  User,
  Building
} from 'lucide-react';
import HelpTip from '../components/HelpTip';
import KanbanBoard from '../components/KanbanBoard';
import { toast } from 'sonner';

const Ideas = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAdmin, isSubmitter } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [l2ForFilter, setL2ForFilter] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showBatchImport, setShowBatchImport] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [processFilter, setProcessFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [processes, setProcesses] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'list');
  const [kanbanGroupBy, setKanbanGroupBy] = useState('status');
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchIdeas();
    fetchDepartments();
    fetchApprovers();
  }, [statusFilter, departmentFilter, typeFilter]);

  const fetchIdeas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (departmentFilter) params.append('department', departmentFilter);
      if (typeFilter) params.append('idea_type', typeFilter);
      
      const response = await api.get(`/ideas?${params.toString()}`);
      setIdeas(response.data);
    } catch (error) {
      toast.error('Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const [deptRes, procRes] = await Promise.all([
        api.get('/settings/departments'),
        api.get('/processes'),
      ]);
      setDepartments(deptRes.data);
      setL2ForFilter(procRes.data.filter(p => p.level === 2));
      setProcesses(procRes.data.filter(p => p.level >= 3));
    } catch (error) {
      console.error('Failed to fetch departments');
    }
  };

  const fetchApprovers = async () => {
    try {
      const response = await api.get('/users/approvers');
      setApprovers(response.data);
    } catch (error) {
      console.error('Failed to fetch approvers');
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/export/ideas');
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredIdeas = ideas
    .filter(idea => {
      // Submitters only see their own ideas
      if (isSubmitter() && idea.submitter_id !== user?.id) return false;
      if (processFilter && processFilter !== 'all' && idea.linked_process_id !== processFilter) return false;
      if (!searchQuery) return true;
      return (
        idea.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.problem_statement.toLowerCase().includes(searchQuery.toLowerCase()) ||
        idea.submitter_name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'priority_score') {
        aVal = a.effort_cost?.priority_score || 0;
        bVal = b.effort_cost?.priority_score || 0;
      }
      if (sortField === 'expected_benefit') {
        // Use financial_tracking for sorting by expected annual benefit
        const getAnnualBenefit = (idea) => {
          const ft = idea.financial_tracking;
          if (!ft) return idea.value_metrics?.annualised_benefit || 0;
          const savings = ft.savings || {};
          return (savings.one_off_expected || 0) + ((savings.ongoing_expected || 0) * 12);
        };
        aVal = getAnnualBenefit(a);
        bVal = getAnnualBenefit(b);
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 inline ml-1" />
      : <ChevronDown className="w-4 h-4 inline ml-1" />;
  };

  const clearFilters = () => {
    setStatusFilter('');
    setDepartmentFilter('');
    setTypeFilter('');
    setProcessFilter('');
    setSearchQuery('');
    setSearchParams({});
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredIdeas.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredIdeas.map(i => i.id)));
  };

  const handleBulkAction = async (action, targetStatus) => {
    if (selectedIds.size === 0) return;
    const label = action === 'delete' ? 'delete' : action === 'submit' ? 'submit' : `change to ${targetStatus}`;
    if (!window.confirm(`${label} ${selectedIds.size} idea(s)?`)) return;
    try {
      const res = await api.post('/ideas/bulk-action', {
        idea_ids: [...selectedIds],
        action,
        target_status: targetStatus || null,
      });
      toast.success(`${res.data.success} updated${res.data.failed ? `, ${res.data.failed} failed` : ''}`);
      setSelectedIds(new Set());
      fetchIdeas();
    } catch (err) {
      toast.error('Bulk action failed');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ideas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Ideas</h1>
          <p className="text-slate-500 mt-1 flex items-center gap-1">Manage and track improvement ideas <HelpTip helpKey="ideas.page" /></p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* View toggle */}
          <div className="flex border rounded-lg overflow-hidden" data-testid="view-toggle">
            <button onClick={() => { setViewMode('list'); setSearchParams(prev => { prev.delete('view'); return prev; }); }} className={`px-3 py-2 text-sm ${viewMode === 'list' ? 'bg-[#235894] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
              <LayoutList className="w-4 h-4" />
            </button>
            <button onClick={() => { setViewMode('kanban'); setSearchParams(prev => { prev.set('view', 'kanban'); return prev; }); }} className={`px-3 py-2 text-sm ${viewMode === 'kanban' ? 'bg-[#235894] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
              <Columns3 className="w-4 h-4" />
            </button>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowBatchImport(true)}
            data-testid="batch-import-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Batch Import
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExport}
            data-testid="export-ideas-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={() => navigate('/ideas/new')}
            className="bg-[#235894] hover:bg-[#1E4B80]"
            data-testid="create-idea-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Idea
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search ideas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-ideas-input"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="Draft">Draft</SelectItem>
                  <SelectItem value="Submitted">Submitted</SelectItem>
                  <SelectItem value="Under Review">Under Review</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="Converted">Converted</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-40" data-testid="department-filter">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {l2ForFilter.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40" data-testid="type-filter">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="Quick Win">Quick Win</SelectItem>
                  <SelectItem value="Tech Project">Tech Project</SelectItem>
                  <SelectItem value="DMAIC">DMAIC</SelectItem>
                </SelectContent>
              </Select>

              <Select value={processFilter} onValueChange={setProcessFilter}>
                <SelectTrigger className="w-48" data-testid="process-filter">
                  <SelectValue placeholder="All Processes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Processes</SelectItem>
                  {processes.map(p => (
                    <SelectItem key={p.id} value={p.id}>L{p.level} {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {(statusFilter || departmentFilter || typeFilter || processFilter || searchQuery) && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && isAdmin() && viewMode === 'list' && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[#235894] text-white rounded-lg" data-testid="bulk-action-bar">
          <CheckSquare className="w-4 h-4" />
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex gap-2 ml-4">
            <Button size="sm" variant="secondary" onClick={() => handleBulkAction('submit')}>Submit</Button>
            <Button size="sm" variant="secondary" onClick={() => handleBulkAction('change_status', 'Approved')}>Approve</Button>
            <Button size="sm" variant="secondary" className="bg-red-500 hover:bg-red-600 text-white" onClick={() => handleBulkAction('delete')}>
              <Trash2 className="w-3 h-3 mr-1" /> Delete
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="ml-auto text-white/80 hover:text-white" onClick={() => setSelectedIds(new Set())}>Clear</Button>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && !loading && (
        <KanbanBoard
          items={filteredIdeas}
          groupByField={kanbanGroupBy}
          groupByOptions={[
            { value: 'status', label: 'Status' },
            { value: 'idea_type', label: 'Type' },
            { value: 'department', label: 'Department' },
          ]}
          columnOrder={
            kanbanGroupBy === 'status' ? ['Draft', 'Submitted', 'Under Review', 'Approved', 'Rejected', 'Converted'] :
            kanbanGroupBy === 'idea_type' ? ['Quick Win', 'Tech Project', 'DMAIC'] :
            [...new Set(filteredIdeas.map(i => i.department || 'Unknown'))]
          }
          onGroupByChange={setKanbanGroupBy}
          onItemDrop={kanbanGroupBy === 'status' ? async (id, newStatus) => {
            try {
              await api.put(`/ideas/${id}`, { status: newStatus });
              fetchIdeas();
              toast.success(`Moved to ${newStatus}`);
            } catch (err) { toast.error('Failed to update status'); }
          } : null}
          cardConfig={{
            title: 'title',
            badges: (item) => [item.idea_type, item.department],
            meta: (item) => [
              { icon: <User className="w-3 h-3" />, text: item.submitter_name },
              item.effort_cost?.priority_score ? { icon: null, text: `P${item.effort_cost.priority_score}` } : null,
            ],
            link: (item) => `/ideas/${item.id}`,
          }}
          entityType="idea"
        />
      )}

      {/* Table View */}
      {viewMode === 'list' && (
      <>
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
            </div>
          ) : filteredIdeas.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <p className="text-lg">No ideas found</p>
              <p className="text-sm">Try adjusting your filters or create a new idea</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    {isAdmin() && (
                      <TableHead className="w-10">
                        <input type="checkbox" checked={selectedIds.size === filteredIdeas.length && filteredIdeas.length > 0} onChange={toggleSelectAll} className="rounded" />
                      </TableHead>
                    )}
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('title')}
                    >
                      Title <SortIcon field="title" />
                    </TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('department')}
                    >
                      Department <SortIcon field="department" />
                    </TableHead>
                    <TableHead>Submitter</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('priority_score')}
                    >
                      Priority <SortIcon field="priority_score" />
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => handleSort('expected_benefit')}
                    >
                      Expected Benefit <SortIcon field="expected_benefit" />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredIdeas.map((idea) => (
                    <TableRow 
                      key={idea.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/ideas/${idea.id}`)}
                      data-testid={`idea-row-${idea.id}`}
                    >
                      {isAdmin() && (
                        <TableCell onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedIds.has(idea.id)} onChange={() => toggleSelect(idea.id)} className="rounded" />
                        </TableCell>
                      )}
                      <TableCell className="font-medium max-w-xs truncate">
                        {idea.title}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={idea.idea_type} />
                      </TableCell>
                      <TableCell>{idea.department}</TableCell>
                      <TableCell>{idea.submitter_name}</TableCell>
                      <TableCell>
                        <StatusBadge status={idea.status} />
                      </TableCell>
                      <TableCell>
                        {idea.effort_cost?.priority_score || '-'}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const ft = idea.financial_tracking;
                          if (ft?.savings) {
                            const annual = (ft.savings.one_off_expected || 0) + ((ft.savings.ongoing_expected || 0) * 12);
                            return annual > 0 ? `£${annual.toLocaleString()}/yr` : '-';
                          }
                          return idea.value_metrics?.annualised_benefit 
                            ? `£${idea.value_metrics.annualised_benefit.toLocaleString()}/yr`
                            : '-';
                        })()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results count */}
      <p className="text-sm text-slate-500">
        Showing {filteredIdeas.length} of {ideas.length} ideas
      </p>
      </>
      )}

      {/* AI Assistant FAB */}
      <Button
        onClick={() => setShowAIAssistant(!showAIAssistant)}
        className="fixed bottom-20 right-6 h-14 w-14 rounded-full bg-gradient-to-r from-[#235894] to-[#26B8A7] hover:from-[#1E4B80] hover:to-[#1fa393] shadow-lg z-40"
        size="icon"
        data-testid="ai-assistant-btn"
      >
        {showAIAssistant ? (
          <Sparkles className="w-6 h-6 text-white" />
        ) : (
          <Bot className="w-6 h-6 text-white" />
        )}
      </Button>

      {/* AI Assistant Panel */}
      {showAIAssistant && (
        <AIAssistant onClose={() => setShowAIAssistant(false)} />
      )}

      {/* Batch Import Modal */}
      {showBatchImport && (
        <BatchImport 
          onImportComplete={() => {
            fetchIdeas();
            setShowBatchImport(false);
          }}
          onClose={() => setShowBatchImport(false)}
        />
      )}
    </div>
  );
};

export default Ideas;
